import {useCallback, useEffect, useMemo, useState} from 'react';

import {Button, Modal, Select, SpinningLoader} from '@/components';
import type {AfriexCountry} from '@/types';

import {CANONICAL_KYC_KEYS, KYC_PROCESSOR_LABELS} from './kycProcessorLabels';

type FormProps = {
  onClose: VoidFunction;
  showModal: boolean;
  handleSave: (id: string, body: Partial<AfriexCountry>) => Promise<void>;
  item: AfriexCountry;
};

const KycSettingsModal = ({
  onClose,
  handleSave,
  item,
  showModal,
}: FormProps) => {
  const [value, setValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setValue(item?.params?.kycProcessor ?? '');
  }, [item?.params?.kycProcessor]);

  const options = useMemo(() => {
    // A country already holding the legacy key (or any value this build does
    // not know about) must still render as itself rather than silently
    // appearing unset, which would invite overwriting it by accident.
    const keys: string[] = [...CANONICAL_KYC_KEYS];
    if (value && !keys.includes(value)) {
      keys.push(value);
    }
    return [
      {key: 'Not set', value: ''},
      ...keys.map((key) => ({
        key: KYC_PROCESSOR_LABELS[key] ?? key,
        value: key,
      })),
    ];
  }, [value]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      // Spread the existing params so saving the KYC processor cannot drop
      // referralParams, which the sibling tab writes to the same subtree.
      const updateData = {
        params: {...item.params, kycProcessor: value || undefined},
      } as Partial<AfriexCountry>;
      setIsLoading(true);
      try {
        await handleSave(String(item?.id), updateData);
      } finally {
        setIsLoading(false);
      }
    },
    [value, handleSave, item],
  );

  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      title={`${item?.name ?? ''} KYC Processor`}
      closeOnOutsideClick
    >
      <div className="mx-5">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col space-y-5">
            <div className="mt-5">
              <Select
                className="w-full"
                options={options}
                value={value}
                label="KYC Processor"
                // The shared Select renders its own placeholder as `disabled`,
                // so the explicit "Not set" option above is what makes clearing
                // the processor possible at all.
                placeholder="Not set"
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setValue(event.target.value)
                }
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Which provider verifies identities in this country. Leaving it
                unset falls back to the global default.
              </p>
            </div>

            <div className="flex w-full justify-end space-x-2 py-5">
              <Button onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button disabled={isLoading} type="submit" variant="solid">
                Submit
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default KycSettingsModal;
